import { useEffect, useRef, useState } from 'react'

import { ApiError, apiGet } from '../lib/api'
import { classifyError, type ErrorKind } from './classifyError'
import { mapTomTomResults, type Result } from './mapResults'

/**
 * useSearch — debounced TomTom Fuzzy Search hook (Plan 02-03 Task 3).
 *
 * Contract (D-63/D-64/D-65/D-78/D-79, UI-SPEC §Components 1-2):
 *   - `query.length < 3` (after trim) → `{ status: 'tooShort' }`, no fetch
 *   - `query.length >= 3` → schedule `apiGet` 250ms after the last change
 *   - Successive keystrokes within 250ms cancel the previous timer
 *   - Each new fetch aborts any in-flight request via AbortController
 *   - Unmount aborts the in-flight request and clears the pending timer
 *   - `AbortError` is swallowed (not surfaced as state 'error' — prevents
 *     banner flicker on rapid typing)
 *   - `ApiError` with Problem URN → `{ status: 'error', kind: classifyError(urn) }`
 *   - URL composed with bias params: `/tomtom/autocomplete?query=...&limit=8
 *     &lat=...&lon=...&radius=...&countrySet=...`
 */

export type SearchBias = {
  lat: number
  lon: number
  radius: number
  countrySet: string
}

export type SearchState =
  | { status: 'idle' }
  | { status: 'tooShort' }
  | { status: 'loading' }
  | { status: 'results'; results: Result[] }
  | { status: 'empty' }
  | { status: 'error'; kind: ErrorKind }

export const DEBOUNCE_MS = 250
export const MIN_QUERY_CHARS = 3
export const DEFAULT_LIMIT = 8

export function useSearch(query: string, opts: { bias: SearchBias }): SearchState {
  const { bias } = opts
  const [state, setState] = useState<SearchState>({ status: 'idle' })
  const abortRef = useRef<AbortController | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const trimmed = query.trim()

    // Cancel any pending timer + in-flight request before starting anew.
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }

    if (trimmed.length === 0) {
      setState({ status: 'idle' })
      return
    }
    if (trimmed.length < MIN_QUERY_CHARS) {
      setState({ status: 'tooShort' })
      return
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      const ac = new AbortController()
      abortRef.current = ac
      setState({ status: 'loading' })

      const path =
        `/tomtom/autocomplete?query=${encodeURIComponent(trimmed)}` +
        `&limit=${DEFAULT_LIMIT}` +
        `&lat=${bias.lat}` +
        `&lon=${bias.lon}` +
        `&radius=${bias.radius}` +
        `&countrySet=${encodeURIComponent(bias.countrySet)}`

      apiGet(path, undefined, ac.signal)
        .then((raw) => {
          if (ac.signal.aborted) return
          const results = mapTomTomResults(raw)
          setState(
            results.length === 0
              ? { status: 'empty' }
              : { status: 'results', results },
          )
        })
        .catch((err: unknown) => {
          if (ac.signal.aborted) return
          // Swallow AbortError — this branch only runs if the caller passed
          // a different AbortController that fired, but guard defensively.
          if (err instanceof Error && err.name === 'AbortError') return
          if (err instanceof ApiError) {
            setState({ status: 'error', kind: classifyError(err.urn) })
            return
          }
          setState({ status: 'error', kind: 'unknown' })
        })
    }, DEBOUNCE_MS)

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
    }
  }, [query, bias.lat, bias.lon, bias.radius, bias.countrySet])

  return state
}
